import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Users, User as UserIcon, Shield, Trash2, EyeOff, Save, Loader2 } from 'lucide-react';
import { api, registerDirtySource } from '@onvice/portal-shell-api';
import { BackLink, ConfirmModal, SettingsSection, getModuleLabel, useBack, useDialogs, usePageLabel } from '@onvice/portal-ui';
import { NAV_ITEMS } from '../../components/PortalSidebar';
import { refreshSidebarBlacklist } from '../../lib/useSidebarBlacklist';

type Level = 'admin' | 'editor' | 'author' | 'tester' | 'reader' | 'depositor' | 'none';

const LEVELS: Level[] = ['none', 'reader', 'tester', 'author', 'editor', 'admin'];

interface GroupDetail {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  members: { id: string; first_name: string; last_name: string; email: string }[];
  subgroups: { id: string; name: string }[];
}

interface ModuleAccessRow {
  module_slug: string;
  level: Level;
}

interface PersonOpt { id: string; first_name: string; last_name: string; email: string }

const LEVEL_COLOR: Record<Level, string> = {
  admin:    'bg-brand-500/20 text-brand-300 border-brand-500/30',
  editor:   'bg-sky-500/20 text-sky-300 border-sky-500/30',
  author:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  tester:   'bg-purple-500/20 text-purple-300 border-purple-500/30',
  reader:   'bg-slate-500/20 text-slate-300 border-slate-500/30',
  depositor:'bg-amber-500/20 text-amber-300 border-amber-500/30',
  none:     'bg-slate-800 text-slate-500 border-slate-700',
};

// Sidebar-Punkte, die je Gruppe ausgeblendet werden können (SP1 2026-07-21).
const SIDEBAR_ITEMS: Array<{ slug: string; label: string }> = [
  ...NAV_ITEMS.filter((it) => it.slug !== 'home').map((it) => ({ slug: it.slug, label: it.label })),
  // 2026-08-20: Zusatz-Slugs die kein NAV_ITEMS-Eintrag sind, aber
  // ausserhalb der klassischen Sidebar erreichbar. Der Consumer
  // (AssistantAvatar, MobileHomeTabBar.MORE_ITEMS) filtert selbst
  // gegen die gleiche Blacklist-Union.
  { slug: 'assistant', label: 'Assistent (Avatar oben rechts + Mobile-Mehr)' },
];

/**
 * Gruppen-Detail. Dokument-Navigation 1.44 (Jörg 2026-09-29, E10): jeder Abschnitt zeigt
 * zuerst an, „Bearbeiten" öffnet ihn, „Speichern" und „Abbrechen" führen zurück in die
 * Anzeige (Baustein SettingsSection). Modul-Zugriffe werden gesammelt und mit
 * „Speichern (n)" geschrieben — vorher schrieb jede Auswahl sofort.
 */
export default function AdminAdressbuchGroupDetailPage() {
  const { id } = useParams();
  const dlg = useDialogs();
  const back = useBack({ fallback: '/admin/adressbuch?tab=groups', fallbackLabel: 'Adressbuch' });
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [access, setAccess] = useState<ModuleAccessRow[]>([]);
  const [allPersons, setAllPersons] = useState<PersonOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // SP1 2026-07-21: Gruppen-Sidebar-Blacklist
  const [blacklist, setBlacklist] = useState<string[]>([]);
  usePageLabel(group?.name ?? null, 'document');
  // Ausgangsstände stabil halten — die Bearbeitungs-Sitzung vergleicht sie je Render.
  const stammBaseline = useMemo(() => (group ? { name: group.name, description: group.description ?? '' } : null), [group]);
  const memberBaseline = useMemo(() => (group ? { personIds: group.members.map((m) => m.id).sort() } : null), [group]);
  const sidebarBaseline = useMemo(() => ({ slugs: [...blacklist].sort() }), [blacklist]);

  function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<{ success: boolean; data: GroupDetail }>(`/admin/adressbuch/groups/${id}`),
      api.get<{ success: boolean; data: ModuleAccessRow[] }>(`/admin/adressbuch/groups/${id}/access`),
      api.get<{ success: boolean; data: PersonOpt[] }>(`/admin/adressbuch/persons`),
      api.get<{ success: boolean; data: { slugs: string[] } }>(`/admin/adressbuch/groups/${id}/sidebar-blacklist`),
    ])
      .then(([g, a, p, b]) => {
        setGroup(g.data); setAccess(a.data); setAllPersons(p.data);
        setBlacklist(b.data.slugs ?? []);
      })
      .catch((err: unknown) => setError((err as Error).message ?? 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }
  useEffect(load, [id]);

  async function doDelete() {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/adressbuch/groups/${id}`);
      setConfirmDelete(false);
      // Das Dokument ist weg — zurück dorthin, wo der Anwender herkam (Gruppenliste).
      back.go();
    } catch (err) {
      setConfirmDelete(false);
      await dlg.error('Löschen fehlgeschlagen', err);
    } finally {
      setDeleting(false);
    }
  }

  if (loading && !group) return <div className="px-4 sm:px-8 py-6 text-slate-400">Laden…</div>;
  if (error) {
    return (
      <div className="px-4 sm:px-8 py-6 space-y-3">
        <BackLink variant="text" fallback="/admin/adressbuch?tab=groups" fallbackLabel="Adressbuch" />
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300 flex flex-wrap items-center justify-between gap-2" data-test-anchor="ladefehler">
          <span>Nicht geladen: {error}</span>
          <button type="button" onClick={load} className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs">Erneut laden</button>
        </div>
      </div>
    );
  }
  if (!group) return null;

  return (
    <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto space-y-6" data-test-anchor="group-page">
      <div className="flex items-center gap-3">
        <BackLink fallback="/admin/adressbuch?tab=groups" fallbackLabel="Adressbuch" />
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Users size={20} /> {group.name}
        </h1>
        <span className="text-xs text-muted-foreground ml-auto">
          {group.members.length} {group.members.length === 1 ? 'Person' : 'Personen'}
          {group.subgroups.length > 0 && ` + ${group.subgroups.length} Untergruppen`}
        </span>
      </div>

      <SettingsSection<{ name: string; description: string }>
        id={`gruppe:${group.id}:stammdaten`}
        title="Stammdaten"
        baseline={stammBaseline}
        validate={(v) => (v.name.trim() ? [] : ['Name'])}
        onSave={async (v) => {
          await api.put(`/admin/adressbuch/groups/${id}`, { name: v.name.trim(), description: v.description.trim() || null });
          load();
        }}
        renderRead={(v) => (
          <dl className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-slate-500">Name</dt><dd className="text-slate-100">{v.name}</dd>
            <dt className="text-slate-500">Beschreibung</dt><dd className="text-slate-200 whitespace-pre-wrap">{v.description || <span className="italic text-slate-500">—</span>}</dd>
          </dl>
        )}
        renderEdit={(v, setField) => (
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">Name</span>
              <input
                value={v.name}
                onChange={(e) => setField('name', e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">Beschreibung</span>
              <textarea
                value={v.description}
                onChange={(e) => setField('description', e.target.value)}
                rows={2}
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              />
            </label>
          </div>
        )}
        testAnchor="gruppe-stammdaten"
      />

      <MembersSection
        key={group.updated_at}
        groupId={group.id}
        baseline={memberBaseline}
        members={group.members}
        subgroups={group.subgroups}
        allPersons={allPersons}
        onSaved={load}
      />

      {/* 2026-07-07 (Joerg): Modul-Levels der Gruppe direkt hier editierbar
          (statt Umweg ueber die Modul-Detail-Page). Aendert PUT
          /admin/adressbuch/modules/:slug/access/group/:id — dieselbe API wie im
          Modul-Editor, nur andere UI-Perspektive. */}
      <ModuleAccessSection groupId={group.id} access={access} onSaved={load} />

      {/* SP1 2026-07-21: Sidebar-Blacklist pro Gruppe. Admin blendet Sidebar-
          Eintraege aus, damit Portal beim Kunden schrittweise eingefuehrt werden
          kann. ACL bleibt unveraendert — nur die UI-Sichtbarkeit wird gesteuert.
          Admin+GF (Wildcard) sehen die Blacklist nicht — sie behalten Vollsicht.
          Wirkt tenant-weit (Desktop-Sidebar, Mobile-Tabs, Mobile-Mehr, Home). */}
      <SettingsSection<{ slugs: string[] }>
        id={`gruppe:${group.id}:sidebar`}
        title="Sidebar-Sichtbarkeit dieser Gruppe"
        description="Angehakte Punkte werden für Mitglieder dieser Gruppe ausgeblendet, selbst wenn die Rechte Zugriff erlauben. Admin und GF sehen immer alles. Ideal für einen Rollout in Etappen."
        baseline={sidebarBaseline}
        onSave={async (v) => {
          await api.put(`/admin/adressbuch/groups/${id}/sidebar-blacklist`, { slugs: v.slugs });
          setBlacklist(v.slugs);
          // Der Admin ist meist selbst in keiner der geaenderten Gruppen (Wildcard-
          // Bypass) — aber wenn doch, soll AssistantAvatar / MobileHomeTabBar
          // sofort neu evaluieren statt bis zum naechsten Login zu warten.
          refreshSidebarBlacklist();
        }}
        renderRead={(v) => v.slugs.length === 0
          ? <div className="text-[11px] text-emerald-400/80 flex items-center gap-1.5"><EyeOff size={12} /> Keine Einschränkungen — die Gruppe sieht alle laut Rechten erlaubten Sidebar-Punkte.</div>
          : (
            <div className="flex flex-wrap gap-1.5">
              {v.slugs.map((slug) => (
                <span key={slug} className="text-xs px-2 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 inline-flex items-center gap-1">
                  <EyeOff size={11} /> {SIDEBAR_ITEMS.find((it) => it.slug === slug)?.label ?? slug}
                </span>
              ))}
            </div>
          )}
        renderEdit={(v, setField) => (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {SIDEBAR_ITEMS.map((it) => {
              const hidden = v.slugs.includes(it.slug);
              return (
                <label
                  key={it.slug}
                  className={`flex items-center gap-2 border rounded-md px-2 py-1.5 cursor-pointer text-xs ${
                    hidden
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                      : 'border-default hover:border-primary/40 text-foreground'
                  }`}
                  title={hidden ? 'Ausgeblendet — klicken zum Anzeigen' : 'Sichtbar — klicken zum Ausblenden'}
                >
                  <input
                    type="checkbox"
                    checked={hidden}
                    onChange={() => setField('slugs', (hidden ? v.slugs.filter((s) => s !== it.slug) : [...v.slugs, it.slug]).sort())}
                    className="flex-shrink-0"
                  />
                  <span className="truncate">{it.label}</span>
                </label>
              );
            })}
          </div>
        )}
        testAnchor="gruppe-sidebar"
      />

      <div className="flex justify-start">
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-rose-400 hover:bg-rose-500/10 text-sm min-h-[44px]"
          data-test-anchor="gruppe-loeschen"
        >
          <Trash2 size={14} /> Gruppe löschen
        </button>
      </div>

      <ConfirmModal
        open={confirmDelete}
        title="Gruppe löschen?"
        message={`Die Gruppe "${group.name}" wird endgültig gelöscht. ACL-Einträge dieser Gruppe in allen Modulen werden mit entfernt. Mitgliedschaften (${group.members.length} Personen) werden gelöst.`}
        confirmLabel="Endgültig löschen"
        danger
        busy={deleting}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}

function MembersSection({ groupId, baseline, members, subgroups, allPersons, onSaved }: {
  groupId: string;
  baseline: { personIds: string[] } | null;
  members: GroupDetail['members'];
  subgroups: GroupDetail['subgroups'];
  allPersons: PersonOpt[];
  onSaved: () => void;
}) {
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const visible = q
    ? allPersons.filter((p) =>
        p.first_name.toLowerCase().includes(q) ||
        p.last_name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q))
    : allPersons;

  return (
    <SettingsSection<{ personIds: string[] }>
      id={`gruppe:${groupId}:mitglieder`}
      title="Mitglieder"
      baseline={baseline}
      onSave={async (v) => {
        await api.put(`/admin/adressbuch/groups/${groupId}/members`, { personIds: v.personIds });
        onSaved();
      }}
      renderRead={() => (
        <div className="space-y-3">
          {members.length === 0
            ? <p className="text-xs text-slate-500 italic">Keine Mitglieder</p>
            : (
              <div className="flex flex-wrap gap-1.5">
                {members.map((m) => (
                  <Link key={m.id} to={`/admin/adressbuch/persons/${m.id}`} className="text-[12px] px-2 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-200 hover:border-brand-500/50 inline-flex items-center gap-1">
                    <UserIcon size={11} /> {m.first_name} {m.last_name}
                  </Link>
                ))}
              </div>
            )}
          {subgroups.length > 0 && (
            <>
              <h3 className="text-[12px] font-semibold text-slate-400 mt-3 mb-2">Untergruppen</h3>
              <div className="flex flex-wrap gap-1.5">
                {subgroups.map((sg) => (
                  <Link
                    key={sg.id}
                    to={`/admin/adressbuch/groups/${sg.id}`}
                    className="text-[12px] px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-300 border border-brand-500/20 hover:bg-brand-500/25"
                  >
                    {sg.name}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      renderEdit={(v, setField) => {
        const selected = new Set(v.personIds);
        const toggle = (pid: string) => {
          const next = new Set(selected);
          if (next.has(pid)) next.delete(pid); else next.add(pid);
          setField('personIds', Array.from(next).sort());
        };
        return (
          <div>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Personen filtern…"
              className="w-full h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm mb-3"
            />
            <div className="max-h-96 overflow-y-auto space-y-1">
              {visible.length === 0 ? (
                <p className="text-xs text-slate-500 italic py-2">Keine Treffer</p>
              ) : (
                visible.map((p) => (
                  <label key={p.id} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md border cursor-pointer ${selected.has(p.id) ? 'border-brand-500/40 bg-brand-500/5' : 'border-default hover:border-slate-600'}`}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="rounded" />
                    <span className="flex-1">{p.first_name} {p.last_name}</span>
                    <span className="text-[11px] text-slate-500">{p.email}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        );
      }}
      testAnchor="gruppe-mitglieder"
    />
  );
}

/** Modul-Zugriffe der Gruppe: Auswahl sammeln, „Speichern (n)" schreibt (MAT). */
function ModuleAccessSection({ groupId, access, onSaved }: {
  groupId: string;
  access: ModuleAccessRow[];
  onSaved: () => void;
}) {
  const dlg = useDialogs();
  const [pending, setPending] = useState<Map<string, Level>>(new Map());
  const [saving, setSaving] = useState(false);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(() => { setPending(new Map()); }, [access]);
  useEffect(() => registerDirtySource({
    id: `gruppe:${groupId}:modulzugriff`,
    label: 'Modul-Zugriff der Gruppe',
    isDirty: () => pendingRef.current.size > 0,
    discard: () => setPending(new Map()),
  }), [groupId]);

  const saved = useMemo(() => new Map(access.map((m) => [m.module_slug, m.level] as const)), [access]);
  const setLevel = (slug: string, level: Level) => setPending((prev) => {
    const m = new Map(prev);
    if (level === saved.get(slug)) m.delete(slug); else m.set(slug, level);
    return m;
  });

  async function saveAll() {
    if (pending.size === 0) return;
    setSaving(true);
    const failed: string[] = [];
    const done: string[] = [];
    for (const [slug, level] of pending) {
      try {
        await api.put(`/admin/adressbuch/modules/${slug}/access/group/${groupId}`, { level });
        done.push(slug);
      } catch (e) {
        failed.push(`${getModuleLabel(slug)}: ${(e as Error).message || 'Speichern fehlgeschlagen'}`);
      }
    }
    setPending((prev) => { const m = new Map(prev); for (const k of done) m.delete(k); return m; });
    setSaving(false);
    onSaved();
    if (failed.length > 0) {
      await dlg.notify({ kind: 'error', title: 'Modul-Zugriff teilweise nicht gespeichert', message: 'Diese Module bleiben zum erneuten Speichern markiert:', items: failed });
    }
  }

  return (
    <section className="bg-card border border-default rounded-lg p-4" data-test-anchor="gruppe-modulzugriff" data-dirty={pending.size > 0 ? '1' : '0'}>
      <div className="flex flex-wrap items-start gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            <Shield size={14} /> Modul-Zugriff dieser Gruppe
          </h2>
          <p className="text-[11px] text-slate-500 mt-1">
            Level pro Modul einstellen — Personen dieser Gruppe erben den Level automatisch.
            Geändert wird erst mit „Speichern". Die Modul-Detail-Seite zeigt umgekehrt alle Gruppen + Personen pro Modul.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pending.size > 0 && (
            <button type="button" disabled={saving} onClick={() => setPending(new Map())}
                    className="px-3 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm min-h-[44px] disabled:opacity-50">
              Verwerfen ({pending.size})
            </button>
          )}
          <button type="button" disabled={saving || pending.size === 0} onClick={() => { void saveAll(); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground text-sm min-h-[44px]">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Speichern…' : pending.size === 0 ? 'Keine Änderungen' : `Speichern (${pending.size})`}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {access.map((m) => {
          const level = pending.get(m.module_slug) ?? m.level;
          const changed = pending.has(m.module_slug);
          return (
            <div
              key={m.module_slug}
              className={`flex items-center justify-between gap-2 border rounded-md px-3 py-2 ${changed ? 'border-amber-400' : 'border-default'}`}
            >
              <Link
                to={`/admin/adressbuch/modules/${m.module_slug}`}
                className="flex-1 min-w-0 flex items-center gap-2 hover:text-primary"
                title="Zur Modul-Detail-Seite"
              >
                <span className="text-sm font-medium text-foreground truncate">{getModuleLabel(m.module_slug)}</span>
                <span className="font-mono text-[11px] text-muted-foreground truncate">{m.module_slug}</span>
              </Link>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${LEVEL_COLOR[level]}`}>
                {level}
              </span>
              <select
                value={level}
                disabled={saving}
                onChange={(e) => setLevel(m.module_slug, e.target.value as Level)}
                className="h-8 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-[12px] disabled:opacity-40"
                aria-label={`Level für ${m.module_slug}`}
              >
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          );
        })}
      </div>
    </section>
  );
}
